/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {
  DOMExportOutput,
  EditorConfig,
  ElementFormatType,
  LexicalEditor,
  NodeKey,
  Spread,
} from 'lexical';

import { BlockWithAlignableContents } from '@lexical/react/LexicalBlockWithAlignableContents';
import {
  DecoratorBlockNode,
  SerializedDecoratorBlockNode,
} from '@lexical/react/LexicalDecoratorBlockNode';
import type { JSX } from 'react';
import * as React from 'react';

type EmbedCodeComponentProps = Readonly<{
  className: Readonly<{
    base: string;
    focus: string;
  }>;
  format: ElementFormatType | null;
  nodeKey: NodeKey;
  html: string;
}>;

function EmbedCodeComponent({
  className,
  format,
  nodeKey,
  html,
}: EmbedCodeComponentProps) {
  return (
    <BlockWithAlignableContents
      className={className}
      format={format}
      nodeKey={nodeKey}
    >
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </BlockWithAlignableContents>
  );
}

export type SerializedEmbedCodeNode = Spread<
  {
    html: string;
  },
  SerializedDecoratorBlockNode
>;

export class EmbedCodeNode extends DecoratorBlockNode {
  __html: string;

  static getType(): string {
    return 'embed-iframe';
  }

  static clone(node: EmbedCodeNode): EmbedCodeNode {
    return new EmbedCodeNode(node.__html, node.__format, node.__key);
  }

  static importJSON(serializedNode: SerializedEmbedCodeNode): EmbedCodeNode {
    const node = $createEmbedCodeNode(serializedNode.html);
    node.setFormat(serializedNode.format);
    return node;
  }

  exportJSON(): SerializedEmbedCodeNode {
    return {
      ...super.exportJSON(),
      type: 'embed-iframe',
      version: 1,
      html: this.__html,
    };
  }

  constructor(html: string, format?: ElementFormatType, key?: NodeKey) {
    super(format, key);
    this.__html = html;
  }

  exportDOM(): DOMExportOutput {
    const elementParent = document.createElement('div');
    elementParent.className = 'PlaygroundEditorTheme__embedBlock';
    const element = document.createElement('div');
    element.innerHTML = this.__html;

    elementParent.appendChild(element);
    return { element: elementParent };
  }

  updateDOM(): false {
    return false;
  }

  decorate(_editor: LexicalEditor, config: EditorConfig): JSX.Element {
    const embedBlockTheme = config.theme.embedBlock || {};
    const className = {
      base: embedBlockTheme.base || '',
      focus: embedBlockTheme.focus || '',
    };
    return (
      <EmbedCodeComponent
        className={className}
        format={this.__format}
        nodeKey={this.getKey()}
        html={this.__html}
      />
    );
  }
}

export function $createEmbedCodeNode(html: string): EmbedCodeNode {
  return new EmbedCodeNode(html);
}
